import {
	type Envelope,
	type ErrorCode,
	type RefreshResponse,
	isCredentialVerdict,
	isRefreshable,
	isTransient,
} from "@seagames/contracts";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "../auth/token-store";

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3001/api";

/**
 * Every failure that reaches application code is one of these. The `code` is
 * the contract (CLAUDE.md § Response envelope); `status` is for logging only.
 * Nothing in this app is allowed to branch on `status` — see `fromTransport`.
 */
export class ApiError extends Error {
	constructor(
		readonly code: ErrorCode,
		readonly status: number,
		message: string,
		readonly details?: Record<string, unknown>,
		readonly requestId?: string,
	) {
		super(message);
		this.name = "ApiError";
	}

	/** The dependency did not answer. Keep the tokens, retry with backoff. */
	get transient(): boolean {
		return isTransient(this.code);
	}

	/** The server ruled on the credential. This — and only this — signs the user out. */
	get credentialVerdict(): boolean {
		return isCredentialVerdict(this.code);
	}

	/** Field errors from a 422, keyed by the snake_case field name. */
	get fieldErrors(): Record<string, string[]> {
		const raw = this.details?.["field_errors"];
		return (raw as Record<string, string[]> | undefined) ?? {};
	}
}

/**
 * A failure that never reached the API, or reached it and came back as
 * something other than our envelope — DNS down, connection refused, the API
 * container restarting, a proxy's own HTML 502 page.
 *
 * All of it is "no answer", so all of it becomes SERVICE_UNAVAILABLE. This is
 * the WSL mass-logout lesson applied at the transport layer: the browser's
 * fetch rejection carries no opinion about the user's credential, so it must
 * never be allowed to produce one.
 */
function fromTransport(status: number, requestId?: string): ApiError {
	return new ApiError(
		"SERVICE_UNAVAILABLE",
		status,
		"Can't reach the server right now. Your session is still valid — please try again.",
		undefined,
		requestId,
	);
}

export interface RequestOptions {
	method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
	/** Serialised as JSON. Keys must already be snake_case — the API validates the wire shape. */
	body?: unknown;
	/** Appended as a query string; `undefined` values are dropped. */
	query?: Record<string, string | number | boolean | undefined>;
	signal?: AbortSignal;
	/** Set false for endpoints reached without a session: /auth/login, /auth/refresh, /auth/mfa/*. */
	auth?: boolean;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
	const url = new URL(BASE_URL + path, window.location.origin);
	if (query) {
		for (const [key, value] of Object.entries(query)) {
			if (value !== undefined) url.searchParams.set(key, String(value));
		}
	}
	return url.toString();
}

// ── Refresh, single-flight across every tab ─────────────────────────────────

/**
 * One in-flight refresh at a time IN THIS TAB.
 *
 * Without this, a dashboard that fires six queries on mount with an expired
 * access token sends six refreshes. The API rotates on the first, and the other
 * five present a token that is now the *previous* one. Inside the grace window
 * the server answers them idempotently — but past it, they look exactly like a
 * replayed token, and reuse detection revokes the session. The client would be
 * signing itself out.
 */
let refreshInFlight: Promise<string> | null = null;

/**
 * The same guard, across tabs.
 *
 * Refresh tokens are single-use: using one destroys it. Every tab on this
 * origin shares one localStorage and therefore one token, but each runs its own
 * module instance, so `refreshInFlight` cannot see the others. Two tabs racing
 * is normally absorbed by the API's grace window — until one of them stalls.
 * A tab frozen mid-request (laptop asleep, backgrounded tab) can deliver a
 * refresh minutes after another tab already rotated that token, and the API
 * correctly reads a long-dead token as theft: `REFRESH_REUSE`, session killed,
 * everyone signed out. Observed twice in one afternoon, 2 minutes and 19
 * minutes past the rotation.
 *
 * The Web Locks API is held per ORIGIN, not per tab, and a lock is released
 * when its page goes away — so a frozen tab keeps the lock exactly as long as
 * its request is genuinely still outstanding, and no other tab can rotate
 * underneath it. That is the whole fix: nobody is holding a stale token,
 * because nobody rotated while we waited.
 */
const REFRESH_LOCK = "seagames.auth.refresh";

/**
 * How long to queue behind another tab before giving up on the lock. Well past
 * a healthy refresh; short enough that one wedged tab cannot make this one hang
 * forever. Falling through unlocked is the pre-existing behaviour, not a
 * regression.
 */
const LOCK_WAIT_MS = 15_000;

async function withRefreshLock(run: () => Promise<string>): Promise<string> {
	// Requires a secure context; localhost qualifies. Older browsers just get
	// the per-tab guard.
	if (!navigator.locks) return run();

	const giveUp = new AbortController();
	let timedOut = false;
	const timer = setTimeout(() => {
		timedOut = true;
		giveUp.abort();
	}, LOCK_WAIT_MS);

	try {
		return await navigator.locks.request(REFRESH_LOCK, { signal: giveUp.signal }, run);
	} catch (err) {
		// Only OUR timeout is a reason to proceed unlocked. An abort raised by
		// `run` itself is a real failure and must surface.
		if (timedOut && err instanceof DOMException && err.name === "AbortError") return run();
		throw err;
	} finally {
		clearTimeout(timer);
	}
}

async function performRefresh(): Promise<string> {
	// Read INSIDE the lock. If we queued behind another tab, the token we would
	// have read before waiting is the one that tab just spent.
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		throw new ApiError("TOKEN_MISSING", 401, "Your session has ended. Please sign in again.");
	}

	const result = await rawRequest<RefreshResponse>("/auth/refresh", {
		method: "POST",
		body: { refresh_token: refreshToken },
		auth: false,
	});

	setTokens(
		{ accessToken: result.access_token, refreshToken: result.refresh_token },
		// Belt and braces for the unlocked fallback path: never write a rotation
		// on top of a newer one.
		{ refreshToken },
	);
	return result.access_token;
}

function refreshAccessToken(): Promise<string> {
	refreshInFlight ??= withRefreshLock(performRefresh).finally(() => {
		refreshInFlight = null;
	});
	return refreshInFlight;
}

// ── The transport ───────────────────────────────────────────────────────────

/** One attempt. No refresh, no retry — those live in `request()` above it. */
async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const { method = "GET", body, query, signal, auth = true } = options;

	const headers: Record<string, string> = {};
	if (body !== undefined) headers["Content-Type"] = "application/json";
	if (auth) {
		const token = getAccessToken();
		if (token) headers["Authorization"] = `Bearer ${token}`;
	}

	let response: Response;
	try {
		response = await fetch(buildUrl(path, query), {
			method,
			headers,
			body: body === undefined ? undefined : JSON.stringify(body),
			signal,
			// Bearer-only API. Sending cookies would be meaningless here and the
			// server sets `credentials: false` on CORS anyway.
			credentials: "omit",
		});
	} catch (err) {
		// An aborted request is the caller's own doing, not a server failure.
		if (err instanceof DOMException && err.name === "AbortError") throw err;
		throw fromTransport(0);
	}

	const requestId = response.headers.get("X-Request-Id") ?? undefined;

	// 204: logout, delete-MFA. No body to parse.
	if (response.status === 204) return null as T;

	let envelope: Envelope<T>;
	try {
		envelope = (await response.json()) as Envelope<T>;
	} catch {
		// Not our envelope — an infrastructure error page, or a truncated body.
		throw fromTransport(response.status, requestId);
	}

	if (envelope.success) return envelope.data;

	const { code, message, details } = envelope.error;
	throw new ApiError(code, response.status, message, details, envelope.request_id ?? requestId);
}

/**
 * The one function the rest of the app calls.
 *
 * Handles exactly two things beyond the transport: minting an access token when
 * we hold only a refresh token, and retrying once after TOKEN_EXPIRED. Anything
 * else is handed to the caller with its code intact.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const needsAuth = options.auth !== false;

	// Fresh page load: the access token died with the last document, but the
	// refresh token survived. Mint one BEFORE the request, or the API answers
	// TOKEN_MISSING — a credential verdict — and we sign the user out on reload.
	if (needsAuth && !getAccessToken() && getRefreshToken()) {
		try {
			await refreshAccessToken();
		} catch (err) {
			handleAuthFailure(err);
			throw err;
		}
	}

	try {
		return await rawRequest<T>(path, options);
	} catch (err) {
		if (!(err instanceof ApiError) || !needsAuth) throw err;

		if (isRefreshable(err.code)) {
			try {
				await refreshAccessToken();
			} catch (refreshErr) {
				handleAuthFailure(refreshErr);
				throw refreshErr;
			}
			// Exactly one retry. A second TOKEN_EXPIRED against a token minted
			// milliseconds ago means something is wrong that retrying won't fix.
			return await rawRequest<T>(path, options);
		}

		handleAuthFailure(err);
		throw err;
	}
}

/**
 * The ONLY place tokens are discarded on the client.
 *
 * It asks the code, never the status. `status === 401` is precisely the test
 * that turned a Supabase timeout into 20,000 forced logouts on WSL.
 */
function handleAuthFailure(err: unknown): void {
	if (!(err instanceof ApiError)) return;
	if (!err.credentialVerdict) return;
	clearTokens(err.code === "SESSION_EXPIRED" ? "expired" : "revoked");
}

export const api = {
	get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...options, method: "GET" }),
	post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...options, method: "POST", body }),
	patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...options, method: "PATCH", body }),
	put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...options, method: "PUT", body }),
	delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...options, method: "DELETE", body }),
};
